// ---------------------------------------------------------------------------
// REST card-grid layout store — per-user card order/visibility/size persistence
// ---------------------------------------------------------------------------
//
// Implements the CardGridLayoutStore contract over the API client, against a
// per-consumer `endpoint` (e.g. '/dashboard/layout', '/banners/cards-layout').
// The DEGRADATION invariant lives here: `load` resolves a safe default layout on
// any failure (network/parse/shape) and never throws — a broken layout endpoint
// must not break the page. Normalization against the known card set is the
// caller's concern (it owns knownIds), so `load` returns the raw shape verbatim.

import { isAbortError } from '../core/http/is-abort-error.js';

/** The empty default layout. Empty `order` → caller uses registration order. */
const DEFAULT_LAYOUT = { order: [], hidden: [], sizes: {} };

/**
 * Shape a raw `sizes` payload into typed entries (clamping is left to
 * normalizeLayout — the adapter only fixes types). Ignores arrays/scalars/null.
 * @param {unknown} raw
 * @returns {Record<string, { colSpan: number, rowSpan: number }>}
 */
function shapeSizes(raw) {
  /** @type {Record<string, { colSpan: number, rowSpan: number }>} */
  const out = {};
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    for (const id of Object.keys(/** @type {Record<string, any>} */ (raw))) {
      const entry = /** @type {Record<string, any>} */ (raw)[id];
      if (entry && typeof entry === 'object') {
        out[String(id)] = { colSpan: Number(entry.colSpan), rowSpan: Number(entry.rowSpan) };
      }
    }
  }
  return out;
}

/**
 * @param {{
 *   apiClient: import('../contracts/api.js').ApiClient,
 *   endpoint: string,
 *   logger?: { warn: (msg: string, ctx?: { data?: unknown }) => void },
 * }} deps
 * @returns {import('../contracts/card-grid.js').CardGridLayoutStore}
 */
export function createRestCardGridLayoutStore({ apiClient, endpoint, logger }) {
  return {
    async load(signal) {
      try {
        const raw = await apiClient.get(endpoint, { signal });
        const order = Array.isArray(/** @type {any} */ (raw)?.order)
          ? /** @type {any} */ (raw).order.map(String)
          : [];
        const hidden = Array.isArray(/** @type {any} */ (raw)?.hidden)
          ? /** @type {any} */ (raw).hidden.map(String)
          : [];
        const sizes = shapeSizes(/** @type {any} */ (raw)?.sizes);
        return /** @type {import('../contracts/card-grid.js').CardGridLayout} */ ({ order, hidden, sizes });
      } catch (err) {
        if (isAbortError(err)) throw err; // abort propagates; not a degradation
        logger?.warn('Card grid layout load failed; using default', { data: { endpoint, err } });
        return { ...DEFAULT_LAYOUT };
      }
    },

    async save(layout, signal) {
      await apiClient.put(endpoint, {
        order: layout.order,
        hidden: layout.hidden,
        sizes: layout.sizes,
      }, { signal });
    },
  };
}
